import { useState, useEffect, useCallback, Fragment } from "react";
import {
  FolderOpen, FolderClosed, FileText, Receipt, FileCheck, ChevronDown,
  ChevronRight, Search, LayoutGrid, List, Image as ImageIcon,
  Network, Table2, Download, Loader2, X, Maximize,
} from "lucide-react";
import { axiosInstance } from "../../lib/axios";
import toast from "react-hot-toast";

// ── Types ────────────────────────────────────────────────────────────────────
interface ProjectItem {
  _id: string;
  name: string;
  projectType: string;
  projectStatus: string;
  folderCounts: Record<string, number>;
}

interface FileItem {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  version: string;
  uploadedBy: string;
  mimeType: string;
  category: string;
  description: string;
  changeNotes: string;
  invoiceNumber?: string;
  invoiceType?: string;
}

// ── Constants ────────────────────────────────────────────────────────────────
const STANDARD_FOLDERS = [
  { id: "Shared Documents",            name: "Shared Documents",         icon: FolderOpen  },
  { id: "Proposal & Statement of Work", name: "Proposal & SOW",          icon: FileText    },
  { id: "Invoices",                    name: "Invoices",                  icon: Receipt     },
  { id: "Delivered Files",             name: "Delivered Files",           icon: FileCheck   },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatSize = (bytes: number) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileIconEl = (name: string, cls: string) => {
  if (/\.pdf$/i.test(name))                    return <FileText className={cls} />;
  if (/\.(jpg|jpeg|png|gif|webp)$/i.test(name)) return <ImageIcon className={cls} />;
  if (/\.(xls|xlsx|csv)$/i.test(name))          return <Table2   className={cls} />;
  if (/\.(fig|sketch|xd|ai|eps)$/i.test(name))  return <Network  className={cls} />;
  return <FileText className={cls} />;
};

const getIconColor = (name: string) => {
  if (/\.pdf$/i.test(name))                    return "text-red-400";
  if (/\.(jpg|jpeg|png|gif|webp)$/i.test(name)) return "text-blue-400";
  if (/\.(xls|xlsx|csv)$/i.test(name))          return "text-green-500";
  if (/\.(fig|sketch|xd|ai|eps)$/i.test(name))  return "text-teal-500";
  return "text-outline";
};

// ── Component ────────────────────────────────────────────────────────────────
export default function ClientFilesModule() {
  const [viewMode, setViewMode]               = useState<"grid" | "list">(() => {
    return (localStorage.getItem("filesViewMode") as "grid" | "list") || "grid";
  });
  const [showSearch, setShowSearch]           = useState(false);
  const [searchQuery, setSearchQuery]         = useState("");
  const [sidebarOpen, setSidebarOpen]         = useState(true);

  // Data
  const [activeProjects, setActiveProjects]     = useState<ProjectItem[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<ProjectItem[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder]     = useState("Shared Documents");
  const [files, setFiles]                       = useState<FileItem[]>([]);
  const [selectedFile, setSelectedFile]         = useState<FileItem | null>(null);

  // Track which projects are expanded (all start expanded)
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Loading
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingFiles, setLoadingFiles]       = useState(false);

  // ── Fetch projects ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/client-portal/files/projects");
        setActiveProjects(data.activeProjects || []);
        setArchivedProjects(data.archivedProjects || []);
        const first = data.activeProjects?.[0] || data.archivedProjects?.[0];
        if (first) setSelectedProjectId(first._id);
        // Expand all projects by default
        const allIds = [
          ...(data.activeProjects || []),
          ...(data.archivedProjects || []),
        ].map((p: ProjectItem) => p._id);
        setExpandedProjects(new Set(allIds));
      } catch {
        toast.error("Failed to load projects");
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, []);

  // ── Fetch files ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedProjectId || !selectedFolder) return;
    (async () => {
      setLoadingFiles(true);
      setSelectedFile(null);
      try {
        const { data } = await axiosInstance.get(
          `/client-portal/files/list?projectId=${selectedProjectId}&folderName=${encodeURIComponent(selectedFolder)}`
        );
        setFiles(data.files || []);
      } catch {
        toast.error("Failed to load files");
      } finally {
        setLoadingFiles(false);
      }
    })();
  }, [selectedProjectId, selectedFolder]);

  // ── Persist view mode ───────────────────────────────────────────────────
  const switchView = (mode: "grid" | "list") => {
    setViewMode(mode);
    localStorage.setItem("filesViewMode", mode);
  };

  // ── Download ────────────────────────────────────────────────────────────
  const handleDownload = useCallback(async (fileKey: string) => {
    const id = toast.loading("Preparing download…");
    try {
      const { data } = await axiosInstance.get(
        `/client-portal/files/download?key=${encodeURIComponent(fileKey)}`
      );
      window.open(data.url, "_blank");
      toast.success("Download started", { id });
    } catch {
      toast.error("Failed to download file", { id });
    }
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────
  const allProjects   = [...activeProjects, ...archivedProjects];
  const selectedProject = allProjects.find((p) => p._id === selectedProjectId);

  const filteredFiles = searchQuery
    ? files.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : files;

  // ── Render helpers ───────────────────────────────────────────────────────
  const ProjectList = ({ projects, label }: { projects: ProjectItem[]; label: string }) => (
    <div className="mb-6">
      <h2 className="text-[11px] font-semibold text-tertiary mb-3 uppercase tracking-wider px-1">
        {label} ({projects.length})
      </h2>
      <div className="space-y-1">
        {projects.length === 0 && (
          <p className="text-[12px] text-outline px-2 py-1">No projects</p>
        )}
        {projects.map((p) => {
          const isSelected  = p._id === selectedProjectId;
          const isExpanded  = expandedProjects.has(p._id);
          const totalFiles  = Object.values(p.folderCounts || {}).reduce((a, b) => a + b, 0);

          return (
            <div key={p._id}>
              {/* Project row */}
              <div className={`flex items-center justify-between px-2 py-2 rounded-lg transition-colors ${
                isSelected ? "bg-surface-container" : "hover:bg-surface-container-low"
              }`}>
                {/* Click on name/icon → select project */}
                <button
                  onClick={() => {
                    setSelectedProjectId(p._id);
                    setSelectedFolder("Shared Documents");
                    // Auto-expand when selecting
                    setExpandedProjects((prev) => new Set([...prev, p._id]));
                  }}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left"
                >
                  {isSelected
                    ? <FolderOpen  className="w-4 h-4 text-primary shrink-0" />
                    : <FolderClosed className="w-4 h-4 text-outline shrink-0" />}
                  <span className={`text-[13px] truncate ${isSelected ? "font-semibold text-on-surface" : "text-on-surface-variant"}`}>
                    {p.name}
                  </span>
                </button>

                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Total file count badge */}
                  {totalFiles > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      isSelected ? "bg-primary text-white" : "bg-surface-container text-on-surface-variant"
                    }`}>
                      {totalFiles}
                    </span>
                  )}
                  {/* Independent expand/collapse chevron */}
                  <button
                    onClick={() => toggleExpanded(p._id)}
                    className="p-0.5 text-outline hover:text-on-surface transition-colors"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`} />
                  </button>
                </div>
              </div>

              {/* Subfolders — only shown when expanded */}
              {isExpanded && (
                <div className="pl-5 mt-0.5 space-y-0.5">
                  {STANDARD_FOLDERS.map((f) => {
                    const count  = p.folderCounts?.[f.id] ?? 0;
                    const active = isSelected && selectedFolder === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => {
                          setSelectedProjectId(p._id);
                          setSelectedFolder(f.id);
                        }}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg transition-colors text-left ${
                          active
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-surface-container text-on-surface-variant"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <f.icon className={`w-3.5 h-3.5 ${active ? "text-primary" : "text-outline"}`} />
                          <span className="text-[12px]">{f.name}</span>
                        </div>
                        {count > 0 && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            active ? "bg-primary text-white" : "bg-surface-container text-on-surface-variant"
                          }`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── File card (grid) ─────────────────────────────────────────────────────
  const FileCard = ({ file }: { file: FileItem }) => {
    const selected = selectedFile?.key === file.key;
    const iconColor = getIconColor(file.name);
    return (
      <div
        onClick={() => setSelectedFile(selected ? null : file)}
        className={`group relative bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
          selected ? "border-primary ring-1 ring-primary shadow-md" : "border-outline-variant"
        }`}
      >
        {/* Icon area */}
        <div className="h-28 bg-surface-container-low rounded-lg flex items-center justify-center mb-3">
          {getFileIconEl(file.name, `w-12 h-12 ${iconColor}`)}
        </div>

        {/* Name + category */}
        <h3 className="text-[13px] font-bold text-on-surface truncate mb-0.5" title={file.name}>
          {file.name}
        </h3>
        <p className="text-[11px] text-outline truncate">
          {file.invoiceNumber ? `Invoice ${file.invoiceNumber}` : file.category}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-outline-variant">
          <span className="px-2 py-0.5 bg-secondary-container text-on-secondary-container rounded-full text-[10px] font-bold">
            {file.version}
          </span>
          <span className="text-[11px] text-on-surface-variant">
            {new Date(file.lastModified).toLocaleDateString()}
          </span>
        </div>

        {/* Hover overlay actions */}
        <div className="absolute inset-0 rounded-xl bg-on-background/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(file.key); }}
            className="p-2 bg-white rounded-full shadow text-on-surface hover:text-primary transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col flex-1 h-[calc(100vh-65px)] bg-white overflow-hidden">

      {/* ── Top two-panel row ── */}
      <div className="flex flex-1 min-h-0">

        {/* Sidebar */}
        <aside
          className={`relative shrink-0 bg-surface-container-low border-r border-outline-variant flex flex-col transition-all duration-300 ${
            sidebarOpen ? "w-64" : "w-10"
          }`}
        >
          {/* Toggle tab — always visible on the right edge of the sidebar */}
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            className="absolute -right-0 top-4 z-10 flex items-center justify-center w-6 h-10 bg-surface-container-low border border-outline-variant rounded-r-lg text-on-surface-variant hover:text-primary hover:bg-surface-container transition-colors shadow-sm"
          >
            {sidebarOpen
              ? <ChevronRight className="w-3.5 h-3.5" />
              : <ChevronRight className="w-3.5 h-3.5 rotate-180" />}
          </button>

          {/* Sidebar content — hidden when collapsed */}
          <div className={`flex-1 overflow-y-auto custom-scrollbar p-5 transition-opacity duration-200 ${sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            {loadingProjects ? (
              <div className="flex justify-center items-center h-20">
                <Loader2 className="animate-spin text-primary w-5 h-5" />
              </div>
            ) : (
              <>
                <ProjectList projects={activeProjects}  label="Active Projects"   />
                <ProjectList projects={archivedProjects} label="Archived Projects" />
              </>
            )}
          </div>

          {/* Collapsed state — show folder icons as quick-access buttons */}
          {!sidebarOpen && (
            <div className="flex flex-col items-center pt-16 gap-3 px-1">
              {STANDARD_FOLDERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setSidebarOpen(true); setSelectedFolder(f.id); }}
                  title={f.name}
                  className={`p-2 rounded-lg transition-colors ${
                    selectedFolder === f.id
                      ? "bg-primary/10 text-primary"
                      : "text-outline hover:bg-surface-container hover:text-on-surface"
                  }`}
                >
                  <f.icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Files panel */}
        <section className="flex-1 flex flex-col overflow-hidden">

          {/* Toolbar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant shrink-0">
            <div>
              <nav className="flex items-center text-[11px] text-outline mb-0.5 gap-1">
                <span>{selectedProject?.name || "Select a Project"}</span>
                <ChevronRight className="w-3 h-3" />
                <span className="text-on-surface-variant">{selectedFolder}</span>
              </nav>
              <h2 className="text-[22px] font-bold text-on-surface">{selectedFolder}</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSearch(true)}
                className="p-2 text-on-surface-variant hover:bg-surface-container rounded-full transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
              <div className="flex bg-surface-container-low border border-outline-variant p-0.5 rounded-lg">
                <button
                  onClick={() => switchView("grid")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all ${
                    viewMode === "grid" ? "bg-primary text-white shadow-sm" : "text-on-surface-variant hover:bg-surface-container"
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Grid
                </button>
                <button
                  onClick={() => switchView("list")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all ${
                    viewMode === "list" ? "bg-primary text-white shadow-sm" : "text-on-surface-variant hover:bg-surface-container"
                  }`}
                >
                  <List className="w-3.5 h-3.5" /> List
                </button>
              </div>
            </div>
          </div>

          {/* File content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            {loadingFiles ? (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="animate-spin text-primary w-6 h-6" />
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-outline">
                <FolderOpen className="w-14 h-14 opacity-20 mb-3" />
                <p className="text-[14px] font-semibold">No files in this folder.</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {filteredFiles.map((f) => <FileCard key={f.key} file={f} />)}
              </div>
            ) : (
              <div className="border border-outline-variant rounded-xl overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-surface-container-low">
                    <tr>
                      {["File Name", "Version", "Size", "Uploaded By", "Upload Date"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[11px] font-semibold text-tertiary uppercase">{h}</th>
                      ))}
                      <th className="px-4 py-3 text-[11px] font-semibold text-tertiary uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant">
                    {filteredFiles.map((f) => {
                      const active = selectedFile?.key === f.key;
                      return (
                        <tr
                          key={f.key}
                          onClick={() => setSelectedFile(active ? null : f)}
                          className={`cursor-pointer transition-colors text-[13px] ${
                            active ? "bg-primary/5" : "hover:bg-surface-container-low"
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {getFileIconEl(f.name, `w-4 h-4 ${getIconColor(f.name)}`)}
                              <span className="font-semibold text-on-surface truncate max-w-[180px]" title={f.name}>{f.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-secondary-container text-on-secondary-container rounded-full text-[11px] font-bold">{f.version}</span>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">{formatSize(f.size)}</td>
                          <td className="px-4 py-3 text-on-surface">{f.uploadedBy}</td>
                          <td className="px-4 py-3 text-on-surface-variant">{new Date(f.lastModified).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDownload(f.key); }}
                              className="p-1.5 text-outline hover:text-primary transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── Bottom: File Preview & Details — only shown once a file is selected ── */}
      {selectedFile && (
        <div className="h-[42%] shrink-0 border-t border-outline-variant bg-surface-container-lowest overflow-y-auto custom-scrollbar">
          <div className="flex flex-col lg:flex-row gap-6 p-6 h-full">

            {/* Left 75% */}
            <div className="lg:w-3/4 flex flex-col gap-5">
              <div className="flex flex-col xl:flex-row gap-5">

                {/* Metadata + description */}
                <div className="flex-1 flex flex-col gap-4">
                  <h3 className="text-[16px] font-bold text-on-surface">File Metadata</h3>
                  <div className="bg-surface-container-low border border-outline-variant rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-[13px]">
                      {[
                        ["File Name",  selectedFile.name],
                        ["Project",    selectedProject?.name || "—"],
                        ["Folder",     selectedFolder],
                        ["Type",       selectedFile.name.split(".").pop()?.toUpperCase() || "—"],
                        ["Size",       formatSize(selectedFile.size)],
                        ["Version",    selectedFile.version],
                        ["Uploaded By", selectedFile.uploadedBy],
                        ["Date",       new Date(selectedFile.lastModified).toLocaleDateString()],
                        ...(selectedFile.invoiceNumber ? [
                          ["Invoice #",  selectedFile.invoiceNumber],
                          ["Invoice Type", selectedFile.invoiceType || "—"],
                        ] : []),
                      ].map(([label, val]) => (
                        <Fragment key={String(label)}>
                          <span className="text-outline">{label}</span>
                          <span className={`text-on-surface font-medium truncate ${label === "Version" ? "text-primary font-bold" : ""}`}>{val ?? "—"}</span>
                        </Fragment>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-bold text-on-surface mb-1.5">Description</label>
                      <textarea
                        readOnly
                        value={selectedFile.description || "No description provided."}
                        className="w-full bg-surface-container-low border border-outline-variant rounded-lg p-3 text-[13px] text-on-surface-variant resize-none h-24 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-bold text-on-surface mb-1.5">Change Notes</label>
                      <textarea
                        readOnly
                        value={selectedFile.changeNotes || "No change notes available."}
                        className="w-full bg-surface-container-low border border-outline-variant rounded-lg p-3 text-[13px] text-on-surface-variant resize-none h-24 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Revision history */}
                <div className="flex-1 flex flex-col gap-4">
                  <h3 className="text-[16px] font-bold text-on-surface">Revision History</h3>
                  <div className="border border-outline-variant rounded-xl overflow-hidden bg-white">
                    <table className="w-full text-left">
                      <thead className="bg-surface-container-low">
                        <tr>
                          {["Version", "Uploaded By", "Upload Date", "Revision Notes", "Action"].map((h) => (
                            <th key={h} className="px-4 py-3 text-[11px] font-semibold text-tertiary uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant text-[13px]">
                        <tr className="hover:bg-surface-container-low transition-colors">
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-secondary-container text-on-secondary-container rounded-full text-[11px] font-bold">
                              {selectedFile.version}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-on-surface">{selectedFile.uploadedBy}</td>
                          <td className="px-4 py-3 text-on-surface-variant">
                            {new Date(selectedFile.lastModified).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant text-[12px]">
                            {selectedFile.changeNotes || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleDownload(selectedFile.key)}
                              className="text-outline hover:text-primary transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Right 25% — preview */}
            <div className="lg:w-1/4 flex flex-col gap-3">
              <h3 className="text-[16px] font-bold text-on-surface">File Preview</h3>
              <div className="flex-1 bg-surface-container-low border border-outline-variant rounded-xl flex flex-col items-center justify-center p-4 min-h-[160px]">
                {getFileIconEl(selectedFile.name, `w-16 h-16 opacity-25 ${getIconColor(selectedFile.name)}`)}
                <p className="mt-3 text-[12px] font-semibold text-on-surface-variant text-center break-all">
                  {selectedFile.name}
                </p>
              </div>
              <button
                disabled
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-[12px] font-semibold w-full opacity-50 cursor-not-allowed"
              >
                <Maximize className="w-4 h-4" /> Preview Full Screen
              </button>
              <button
                onClick={() => handleDownload(selectedFile.key)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-on-surface border border-outline-variant rounded-lg hover:bg-surface-container text-[12px] font-semibold w-full transition-colors"
              >
                <Download className="w-4 h-4" /> Download File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Search modal ── */}
      {showSearch && (
        <div className="fixed inset-0 z-[100]">
          <div
            className="absolute inset-0 bg-on-background/40 backdrop-blur-sm"
            onClick={() => { setShowSearch(false); setSearchQuery(""); }}
          />
          <div className="relative top-24 mx-auto max-w-2xl bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-outline-variant">
              <Search className="text-primary w-5 h-5 shrink-0" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none focus:ring-0 text-[20px] font-semibold text-on-surface placeholder:text-outline outline-none"
                placeholder="Search files…"
              />
              <button onClick={() => { setShowSearch(false); setSearchQuery(""); }}>
                <X className="w-5 h-5 text-outline hover:text-on-surface transition-colors" />
              </button>
            </div>
            <div className="px-5 py-3 text-[12px] text-outline">
              {searchQuery
                ? `${filteredFiles.length} result${filteredFiles.length !== 1 ? "s" : ""} in "${selectedFolder}"`
                : "Type to search files in the current folder."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
